import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';
import { filterAndSort, type Repo, type SortKey, type SortDir } from './repo-overview-helpers.js';

type PageSize = 25 | 50 | 100;

const PAGE_SIZES: readonly PageSize[] = [25, 50, 100] as const;
const PAGE_SIZE_STORAGE_KEY = 'repos-overview:page-size';
const HIDE_NO_VERDICT_STORAGE_KEY = 'repos-overview:hide-no-verdict';

const inputStyle: CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)'
};

const thButtonStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'var(--loop-neutral-700)',
  fontWeight: 528,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

function loadInitialPageSize(): PageSize {
  // localStorage can throw in private mode / disabled-storage configurations;
  // fall back to the in-memory default rather than crashing the page.
  if (typeof window === 'undefined') return 50;
  try {
    const raw = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    const n = raw === null ? NaN : Number(raw);
    return (PAGE_SIZES as readonly number[]).includes(n) ? (n as PageSize) : 50;
  } catch {
    return 50;
  }
}

function loadInitialHideNoVerdict(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(HIDE_NO_VERDICT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function SortIndicator({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <span aria-hidden="true" style={{ opacity: 0.3 }}>↕</span>;
  return <span aria-hidden="true">{dir === 'asc' ? '↑' : '↓'}</span>;
}

function ariaSortFor(active: boolean, dir: SortDir): 'ascending' | 'descending' | 'none' {
  if (!active) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

function sortButtonLabel(label: string, active: boolean, dir: SortDir): string {
  if (!active) return `Sort by ${label}`;
  const current = dir === 'asc' ? 'ascending' : 'descending';
  const next = dir === 'asc' ? 'descending' : 'ascending';
  return `Sort by ${label}, currently ${current}; click to sort ${next}`;
}

export function RepoOverview() {
  const [orgFilter, setOrgFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('last_verdict_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [pageSize, setPageSize] = useState<PageSize>(loadInitialPageSize);
  // Pagination is stored alongside the filter signature it belongs to, so that
  // changing any filter implicitly resets to page 1 without an effect that
  // would setState during commit and cascade an extra render.
  const [page, setPage] = useState<{ key: string; index: number }>({ key: '', index: 0 });
  const [hideNoVerdict, setHideNoVerdict] = useState<boolean>(loadInitialHideNoVerdict);

  const { data, isLoading, error } = useApiQuery<Repo[]>(['repos'], '/api/repos');

  const orgs = useMemo(
    () => [...new Set((data ?? []).map(r => r.org))].sort(),
    [data]
  );

  const rows = useMemo(
    () => filterAndSort(data ?? [], { orgFilter, search, sortKey, sortDir, hideNoVerdict }),
    [data, orgFilter, search, sortKey, sortDir, hideNoVerdict]
  );

  // Reset to page 1 whenever filtering/sorting/page-size changes the result set:
  // a stored page only applies to the filter signature it was set under.
  const filterKey = JSON.stringify([orgFilter, search, sortKey, sortDir, pageSize, hideNoVerdict]);
  const pageIndex = page.key === filterKey ? page.index : 0;
  const setPageIndex = useCallback(
    (next: number | ((prev: number) => number)) => {
      setPage(prev => {
        const base = prev.key === filterKey ? prev.index : 0;
        return { key: filterKey, index: typeof next === 'function' ? next(base) : next };
      });
    },
    [filterKey]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
    } catch {
      // Storage disabled / over quota — non-essential; persistence is best-effort.
    }
  }, [pageSize]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(HIDE_NO_VERDICT_STORAGE_KEY, hideNoVerdict ? '1' : '0');
    } catch {
      // Best-effort persistence.
    }
  }, [hideNoVerdict]);

  const total = rows.length;
  const totalUnfiltered = (data ?? []).length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(pageIndex, pageCount - 1);
  const pageStart = safePage * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, total);
  const pageRows = rows.slice(pageStart, pageEnd);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'last_verdict_at' ? 'desc' : 'asc');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>Repos</h2>
        <ConceptHelpIcon />
      </div>

      <p style={{ color: 'var(--loop-neutral-600)', fontSize: '0.8rem', marginBottom: 'var(--loop-space-200)' }}>
        Per-repo DV mode is now managed via the <Link to="/config" style={{ color: 'var(--loop-brand-600)' }}>Configuration</Link> page. This view shows repos with baseline data + their last DV verdict.
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={orgFilter}
          onChange={e => setOrgFilter(e.target.value)}
          style={{ ...inputStyle, width: 192 }}
          aria-label="Filter by org"
        >
          <option value="">All orgs</option>
          {orgs.map(o => <option key={o} value={o}>{o}</option>)}
        </select>

        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search repo name…"
          style={{ ...inputStyle, width: 240 }}
          aria-label="Search repo name"
        />

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--loop-neutral-1100)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={hideNoVerdict}
            onChange={e => setHideNoVerdict(e.target.checked)}
          />
          Hide repos with no verdict
        </label>

        <span style={{ color: 'var(--loop-neutral-600)', fontSize: '0.8rem' }}>
          {total === totalUnfiltered
            ? `${total} repo${total === 1 ? '' : 's'}`
            : `Showing ${total} of ${totalUnfiltered}`}
        </span>
      </div>

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error && <p className="error">Error: {String(error)}</p>}

      {!isLoading && !error && (
        <>
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
              <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
                <tr>
                  <th
                    scope="col"
                    aria-sort={ariaSortFor(sortKey === 'org', sortDir)}
                    style={{ padding: '8px 16px' }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort('org')}
                      style={thButtonStyle}
                      aria-label={sortButtonLabel('Org', sortKey === 'org', sortDir)}
                    >
                      Org <SortIndicator active={sortKey === 'org'} dir={sortDir} />
                    </button>
                  </th>
                  <th
                    scope="col"
                    aria-sort={ariaSortFor(sortKey === 'repo', sortDir)}
                    style={{ padding: '8px 16px' }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort('repo')}
                      style={thButtonStyle}
                      aria-label={sortButtonLabel('Repo', sortKey === 'repo', sortDir)}
                    >
                      Repo <SortIndicator active={sortKey === 'repo'} dir={sortDir} />
                    </button>
                  </th>
                  <th
                    scope="col"
                    aria-sort={ariaSortFor(sortKey === 'last_verdict_at', sortDir)}
                    style={{ padding: '8px 16px' }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort('last_verdict_at')}
                      style={thButtonStyle}
                      aria-label={sortButtonLabel('Last Verdict', sortKey === 'last_verdict_at', sortDir)}
                    >
                      Last Verdict <SortIndicator active={sortKey === 'last_verdict_at'} dir={sortDir} />
                    </button>
                  </th>
                  <th scope="col" style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }} />
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--loop-neutral-500)' }}>
                      No repos found.
                    </td>
                  </tr>
                )}
                {pageRows.map(r => (
                  <tr key={`${r.org}/${r.repo}`} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                    <td style={{ padding: '8px 16px' }}>{r.org}</td>
                    <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.repo}</td>
                    <td style={{ padding: '8px 16px', color: 'var(--loop-neutral-600)' }}>
                      {r.last_verdict_at
                        ? new Date(r.last_verdict_at).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })
                        : '—'}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <Link to={`/repos/${r.org}/${r.repo}`} className="btn-secondary" style={{ fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
                        View <span aria-hidden="true">→</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.8rem', color: 'var(--loop-neutral-700)' }}>
              <span>Showing {pageStart + 1}–{pageEnd} of {total}</span>

              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setPageIndex(p => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  style={{ fontSize: '0.75rem' }}
                >
                  ← Prev
                </button>
                <span>Page {safePage + 1} of {pageCount}</span>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setPageIndex(p => Math.min(pageCount - 1, p + 1))}
                  disabled={safePage >= pageCount - 1}
                  style={{ fontSize: '0.75rem' }}
                >
                  Next →
                </button>
                <label style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  Per page
                  <select
                    value={pageSize}
                    onChange={e => setPageSize(Number(e.target.value) as PageSize)}
                    style={{ ...inputStyle, padding: '4px 8px', fontSize: '0.75rem' }}
                  >
                    {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
